import type {
  Vertex,
  Edge,
  FluidZone,
  PipeSpecification,
  HeatTransferSettings,
  Layer,
  CustomFieldDefinition,
} from '@shared/hydraulic'
import { nextVertexId, nextEdgeId } from './id-generator'

export interface RemapSource {
  vertices: Vertex[]
  edges: Edge[]
  fluid_zones?: FluidZone[]
  pipe_specifications?: PipeSpecification[]
  heat_transfer_settings?: HeatTransferSettings[]
  layers?: Layer[]
  custom_field_definitions?: CustomFieldDefinition[]
}

export interface RemapTarget {
  vertices: Vertex[]
  edges: Edge[]
  layers?: Layer[]
}

export interface RemappedElements {
  vertices: Vertex[]
  edges: Edge[]
  fluid_zones: FluidZone[]
  pipe_specifications: PipeSpecification[]
  heat_transfer_settings: HeatTransferSettings[]
  layers: Layer[]
  custom_field_definitions: CustomFieldDefinition[]
}

const uuid = (): string => crypto.randomUUID()

/** Passthrough-on-miss: returns the mapped value when found, else returns the
 *  original id unchanged (never drops a reference). */
const pass = (m: Map<string, string>, id: string | undefined): string | undefined =>
  id === undefined ? undefined : (m.get(id) ?? id)

/** Rebuild a custom_field_values record remapping each key, with passthrough-on-miss. */
const remapCfv = (
  cfv: Record<string, unknown> | undefined,
  cfdMap: Map<string, string>,
): Record<string, unknown> | undefined => {
  if (cfv === undefined) return undefined
  return Object.fromEntries(Object.entries(cfv).map(([k, val]) => [cfdMap.get(k) ?? k, val]))
}

/** Offset a position by the drop delta. */
const offset = (p: { x: number; y: number }, drop: { x: number; y: number }): { x: number; y: number } => ({
  x: p.x + drop.x,
  y: p.y + drop.y,
})

/**
 * Generic remapper for inserting a snapshot (or paste clipboard) into a target
 * model.  Mints fresh IDs seeded from the TARGET model's existing IDs (so new
 * IDs never collide with anything already in the target), rewrites every
 * ID-bearing cross-reference in the closure table, offsets positions to the
 * drop point, and deduplicates layers by name.
 *
 * All rewrites are passthrough-on-miss (`map.get(id) ?? id`): when source has
 * no reference-data arrays (the paste case), every fluid_zone_id / layer_id /
 * custom_field_values key is left unchanged.
 */
export function remapElements(
  source: RemapSource,
  target: RemapTarget,
  dropPosition: { x: number; y: number },
): RemappedElements {
  // -----------------------------------------------------------------------
  // 1. Mint fresh vertex/edge IDs seeded from the TARGET model.
  // -----------------------------------------------------------------------
  const claimed = new Set<string>()
  const vMap = new Map<string, string>()
  const eMap = new Map<string, string>()

  for (const v of source.vertices) {
    const id = nextVertexId(target.vertices, claimed)
    claimed.add(id)
    vMap.set(v.id, id)
  }

  for (const e of source.edges) {
    const id = nextEdgeId(target.edges, claimed)
    claimed.add(id)
    eMap.set(e.id, id)
  }

  // -----------------------------------------------------------------------
  // 2. Build reference-data id maps.  All reference data except layers is
  //    always-cloned with fresh UUIDs.  Layers dedup-by-name.
  // -----------------------------------------------------------------------
  const fzMap = new Map<string, string>()
  const psMap = new Map<string, string>()
  const sizeMap = new Map<string, string>()
  const htsMap = new Map<string, string>()
  const layerMap = new Map<string, string>()
  const cfdMap = new Map<string, string>()

  const clonedFz: FluidZone[] = (source.fluid_zones ?? []).map(z => {
    const id = uuid()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fzMap.set((z as any).id, id)
    return { ...structuredClone(z), id }
  })

  const clonedPs: PipeSpecification[] = (source.pipe_specifications ?? []).map(s => {
    const id = uuid()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    psMap.set((s as any).id, id)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sizes = ((s as any).sizes ?? []).map((sz: any) => {
      const sid = uuid()
      sizeMap.set(sz.id, sid)
      return { ...structuredClone(sz), id: sid }
    })
    return { ...structuredClone(s), id, sizes }
  })

  const clonedHts: HeatTransferSettings[] = (source.heat_transfer_settings ?? []).map(h => {
    const id = uuid()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    htsMap.set((h as any).id, id)
    return { ...structuredClone(h), id }
  })

  const clonedCfd: CustomFieldDefinition[] = (source.custom_field_definitions ?? []).map(c => {
    const id = uuid()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cfdMap.set((c as any).id, id)
    return { ...structuredClone(c), id }
  })

  // Layer dedup-by-name: if the target already has a layer with the same name,
  // map the source layer's id to the existing layer's id (reuse, no clone).
  // Only clone when no name match exists.
  const targetLayersByName = new Map<string, string>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (target.layers ?? []).map(l => [(l as any).name as string, (l as any).id as string]),
  )
  const clonedLayers: Layer[] = []
  for (const l of source.layers ?? []) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const srcId = (l as any).id as string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const srcName = (l as any).name as string
    const existing = targetLayersByName.get(srcName)
    if (existing !== undefined) {
      // Dedup: reuse the existing target layer — do NOT clone.
      layerMap.set(srcId, existing)
    } else {
      const id = uuid()
      layerMap.set(srcId, id)
      clonedLayers.push({ ...structuredClone(l), id })
    }
  }

  // -----------------------------------------------------------------------
  // 3. Rewrite all ID-bearing fields in each vertex.
  // -----------------------------------------------------------------------
  const vertices: Vertex[] = source.vertices.map(orig => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v: any = structuredClone(orig)
    v.id = vMap.get(orig.id)!
    v.diagram = {
      ...(v.diagram ?? {}),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      position: offset((orig as any).diagram?.position ?? { x: 0, y: 0 }, dropPosition),
    }
    if (v.layer_id !== undefined) v.layer_id = pass(layerMap, v.layer_id)
    if (v.custom_field_values !== undefined) {
      v.custom_field_values = remapCfv(v.custom_field_values as Record<string, unknown>, cfdMap)
    }
    if (v.type === 'tank') {
      if (v.fluid_zone_id !== undefined) v.fluid_zone_id = pass(fzMap, v.fluid_zone_id as string)
      if (Array.isArray(v.penetrations)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        v.penetrations = v.penetrations.map((p: any) => ({ ...p, edge_id: pass(eMap, p.edge_id as string) }))
      }
    }
    if (v.type === 'three_way_valve') {
      for (const f of ['port_a_edge_id', 'port_b_edge_id']) {
        if (v[f] !== undefined) v[f] = pass(eMap, v[f] as string)
      }
    }
    if (v.linked_device_id !== undefined) v.linked_device_id = pass(vMap, v.linked_device_id as string)
    if (v.operation?.linked_device_id !== undefined) {
      v.operation.linked_device_id = pass(vMap, v.operation.linked_device_id as string)
    }
    // Heat exchanger: unmodeled_side fluid zone references.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const u = v.thermal_calculation?.unmodeled_side as any
    if (u !== undefined && u !== null) {
      for (const f of ['inlet_fluid_zone_id', 'outlet_fluid_zone_id']) {
        if (u[f] !== undefined) u[f] = pass(fzMap, u[f] as string)
      }
    }
    return v as Vertex
  })

  // -----------------------------------------------------------------------
  // 4. Rewrite all ID-bearing fields in each edge.
  // -----------------------------------------------------------------------
  const edges: Edge[] = source.edges.map(orig => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const e: any = structuredClone(orig)
    e.id = eMap.get(orig.id)!
    e.connection = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      source_id: pass(vMap, (orig as any).connection.source_id as string)!,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      target_id: pass(vMap, (orig as any).connection.target_id as string)!,
    }
    if (e.fluid_zone_id !== undefined) e.fluid_zone_id = pass(fzMap, e.fluid_zone_id as string)
    if (e.pipe_specification_id !== undefined) e.pipe_specification_id = pass(psMap, e.pipe_specification_id as string)
    if (e.pipe_size_id !== undefined) e.pipe_size_id = pass(sizeMap, e.pipe_size_id as string)
    if (e.heat_transfer_settings_id !== undefined) {
      e.heat_transfer_settings_id = pass(htsMap, e.heat_transfer_settings_id as string)
    }
    if (e.layer_id !== undefined) e.layer_id = pass(layerMap, e.layer_id as string)
    // Waypoints are persisted world-coordinate intent that SURVIVES reroutes —
    // unlike stale `points` (which self-heal at the first reroute), untranslated
    // waypoints would permanently route the pasted pipe through the ORIGINAL
    // copy's location. Translate both; points for instant visual correctness.
    if (e.diagram?.waypoints !== undefined) {
      e.diagram.waypoints = (e.diagram.waypoints as Array<{ x: number; y: number }>).map(p => offset(p, dropPosition))
    }
    if (Array.isArray(e.diagram?.points)) {
      e.diagram.points = (e.diagram.points as Array<{ x: number; y: number }>).map(p => offset(p, dropPosition))
    }
    if (e.custom_field_values !== undefined) {
      e.custom_field_values = remapCfv(e.custom_field_values as Record<string, unknown>, cfdMap)
    }
    return e as Edge
  })

  return {
    vertices,
    edges,
    fluid_zones: clonedFz,
    pipe_specifications: clonedPs,
    heat_transfer_settings: clonedHts,
    layers: clonedLayers,
    custom_field_definitions: clonedCfd,
  }
}
